/**
 * docsRegistry — the per-route help-doc slug registry (Day 100b.2).
 *
 * Maps every arbi-root route to a BARE doc slug (filename stem per the loader
 * scheme; the docs/help/<domain>/ FOLDER carries the domain, the slug never
 * does). Keys are route PREFIXES; resolveDocSlug() picks the LONGEST matching
 * prefix — the same longest-prefix approach as canAccess() in src/lib/roles.ts —
 * so a per-route entry (e.g. the Buying Session how-to) overrides its domain
 * fallback. Per-route specificity where a distinct doc warrants it; every other
 * route falls back to its nav-domain collection slug (DOC_STANDARDS §6).
 *
 * This map is the COMPLETENESS substrate for CLAUDE.md §17 Convention 48: every
 * App.tsx route resolves to a slug, and a slug resolves to either a published
 * docs_pages row or a per-slug coming-soon. Most slugs are coming-soon until the
 * corpus is authored (Day 103b) — the registry's job here is the mapping. The
 * authoritative route list lives in App.tsx <Routes> (JSX, not importable); the
 * coverage test (src/test/day100b2DocsDiscovery.test.ts) enumerates it and
 * asserts every route resolves here.
 *
 * NOTE: DOC_STANDARDS §6 enumerates the authoring collections; the registry must
 * additionally cover every app nav domain (Dashboard / Catalog / Listings /
 * Transfers included, which §6 omits), so those carry forward-looking
 * <domain>-overview slugs. Keep this map in sync with App.tsx <Routes>.
 */

// Route prefix → bare doc slug. Order is irrelevant (longest-prefix wins).
export const ROUTE_DOC_SLUGS: Record<string, string> = {
  // ── Per-route specific docs (a distinct how-to/reference earns its own slug) ──
  // The only published seed today; lives in docs/help/buying-session/.
  '/purchasing/buying-session': 'start-a-buying-session',

  // ── Domain-level fallbacks (the nav-domain collection slug — DOC_STANDARDS §6) ──
  '/dashboard': 'dashboard-overview',
  '/tasks': 'tasks-overview',
  '/files': 'files-overview',
  '/catalog': 'catalog-overview', // no bare /catalog route — covers /catalog/products + /catalog/flagged
  '/purchasing': 'purchasing-overview',
  '/inventory': 'inventory-overview',
  '/listings': 'listings-overview',
  '/fulfillment': 'fulfillment-overview',
  '/returns': 'returns-overview',
  '/transfers': 'transfers-overview', // no bare /transfers route — covers /transfers/inbound + /removals
  '/finance': 'finance-overview',
  '/reports': 'reports-overview',
  '/directory': 'directory-overview',
  '/settings': 'settings-overview',
};

/**
 * Resolve a route pathname to its doc slug via longest-prefix match (mirrors
 * canAccess() in src/lib/roles.ts). Returns null for paths outside the app
 * surface (e.g. /login, /reset-password, the 404 catch-all) — those carry no
 * help doc.
 */
export function resolveDocSlug(path: string): string | null {
  const match = Object.keys(ROUTE_DOC_SLUGS)
    .filter((prefix) => path.startsWith(prefix))
    .sort((a, b) => b.length - a.length)[0];
  return match ? ROUTE_DOC_SLUGS[match] : null;
}
